import { useEffect, useState, useRef } from "react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CreateLoaderTable } from "@/components/Loaders";
import { Pagination } from "@/components/Pagination.tsx";
import { If } from "@/components/If";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/auth";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import {
  Package,
  Search,
  Printer,
  Edit,
  Filter,
  X,
  Truck,
} from "lucide-react";
import {
  getSedexSales,
  getSedexStatuses,
  updateSedexStatus,
  getPrintLabel,
  SedexSaleStatus,
  SedexSaleFiltersParams,
} from "@/Api/sedexSales";
import { getSelectSellers } from "@/Api/sellers";
import { useReactToPrint } from "react-to-print";

interface SedexSale {
  id: number;
  uuid: string;
  total: string;
  tracking_code: string | null;
  expected_arrival_date: string | null;
  sedex_status_id: number | null;
  sale_status_id: number;
  sale_type_id: number;
  created_at: string;
  updated_at: string;
  client: {
    id: number;
    name: string;
    phone: string;
    addresses: Array<{
      street: string | null;
      number: string | null;
      complement: string | null;
      neighborhood: string | null;
      city: string | null;
      state: string | null;
      zip_code: string | null;
    }>;
  };
  seller: {
    id: number;
    user: {
      name: string;
    };
  };
  unity: {
    id: number;
    name: string;
  };
  sedex_status: SedexSaleStatus | null;
  sale_status: {
    id: number;
    name: string;
  };
  sale_type?: {
    id: number;
    name: string;
  };
  updated_by?: {
    id: number;
    name: string;
  };
}

// Status de venda
const SALE_STATUS = {
  IN_PROGRESS: 1,
  APPROVED: 2,
  FAILED: 3,
  CANCELED: 4,
  EDITED: 5,
} as const;

interface PrintLabelData {
  sale_id: number;
  client_name: string;
  address: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  } | null;
  tracking_code: string | null;
}

export const PageList = () => {
  const user = useAuth();
  const { hasPermission } = usePermissions();
  const unityId = user?.user?.unity?.[0]?.id || null;

  const [listData, setListData] = useState<any>({});
  const [state, setState] = useState("loading");
  const [sedexStatuses, setSedexStatuses] = useState<SedexSaleStatus[]>([]);
  const [sellers, setSellers] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const today = Mask.getLocalDate();
  const [datePeriod, setDatePeriod] = useState<string>("today");
  const [filters, setFilters] = useState<SedexSaleFiltersParams>({
    unity_id: unityId?.toString(),
    start_date: today,
    end_date: today,
    sedex_status_id: [],
    seller_id: [],
  });

  // Função para calcular datas do período usando data local
  const getLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDatePeriodChange = (period: string) => {
    setDatePeriod(period);
    const now = new Date();

    let start_date = "";
    let end_date = "";

    if (period === "today") {
      start_date = getLocalDateString(now);
      end_date = getLocalDateString(now);
    } else if (period === "yesterday") {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      start_date = getLocalDateString(yesterday);
      end_date = getLocalDateString(yesterday);
    } else if (period === "thisWeek") {
      const startOfWeek = new Date(now);
      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startOfWeek.setDate(now.getDate() - diff);
      start_date = getLocalDateString(startOfWeek);
      end_date = getLocalDateString(now);
    } else if (period === "thisMonth") {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      start_date = getLocalDateString(startOfMonth);
      end_date = getLocalDateString(now);
    } else if (period === "last7days") {
      const last7Days = new Date(now);
      last7Days.setDate(now.getDate() - 7);
      start_date = getLocalDateString(last7Days);
      end_date = getLocalDateString(now);
    } else if (period === "custom") {
      // Mantém as datas atuais para personalizado
      return;
    }

    setFilters((prev) => ({
      ...prev,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
    }));
  };

  // Modal de editar status
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<SedexSale | null>(null);
  const [editForm, setEditForm] = useState({
    sedex_status_id: "",
    tracking_code: "",
    expected_arrival_date: "",
  });

  // Modal de impressão
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printData, setPrintData] = useState<PrintLabelData | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: A5 portrait;
        margin: 8mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
      }
    `,
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [statusesRes, sellersRes] = await Promise.all([
        getSedexStatuses(),
        getSelectSellers({ is_active: true }),
      ]);

      if (statusesRes.ok) setSedexStatuses(statusesRes.data?.response || []);
      if (sellersRes.ok) setSellers(sellersRes.data?.response || []);

      await fetchSales();
    } catch (error) {
      console.error("Error loading initial data:", error);
      setState("production");
    }
  };

  const fetchSales = async (page: number = 1) => {
    setState("loading");
    try {
      const { data, ok } = await getSedexSales(page, filters);
      if (ok) {
        setListData(data?.response || {});
      }
    } catch (error) {
      console.error("Error fetching sedex sales:", error);
    } finally {
      setState("production");
    }
  };

  const handleApplyFilters = () => {
    fetchSales(1);
  };

  const handleClearFilters = () => {
    const todayDate = Mask.getLocalDate();
    setDatePeriod("today");
    setFilters({
      unity_id: unityId?.toString(),
      start_date: todayDate,
      end_date: todayDate,
      sedex_status_id: [],
      seller_id: [],
      only_edited: false,
    });
    fetchSales(1);
  };

  const openEditModal = (sale: SedexSale) => {
    setSelectedSale(sale);
    setEditForm({
      sedex_status_id: sale.sedex_status_id?.toString() || "",
      tracking_code: sale.tracking_code || "",
      expected_arrival_date: sale.expected_arrival_date || "",
    });
    setEditModalOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedSale) return;

    try {
      const { ok, data } = await updateSedexStatus(selectedSale.uuid, {
        sedex_status_id: editForm.sedex_status_id
          ? parseInt(editForm.sedex_status_id)
          : undefined,
        tracking_code: editForm.tracking_code || undefined,
        expected_arrival_date: editForm.expected_arrival_date || undefined,
      });

      if (ok) {
        toast.success("Status atualizado com sucesso!");
        setEditModalOpen(false);
        fetchSales(listData?.current_page || 1);
      } else {
        toast.error(data?.message || "Erro ao atualizar status");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao atualizar status");
    }
  };

  const openPrintModal = async (sale: SedexSale) => {
    try {
      const { ok, data } = await getPrintLabel(sale.uuid);
      if (ok) {
        setPrintData(data?.response);
        setPrintModalOpen(true);
      } else {
        toast.error("Erro ao carregar dados para impressão");
      }
    } catch (error) {
      toast.error("Erro ao carregar dados para impressão");
    }
  };

  const getStatusBadgeColor = (statusId: number | null) => {
    switch (statusId) {
      case 1:
        return "bg-yellow-500";
      case 2:
        return "bg-blue-500";
      case 3:
        return "bg-green-500";
      case 4:
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Main>
      <If condition={hasPermission("view-sedex-tab")}>
        <PageHeader
          icon={Package}
          title="Sedex"
          description="Gerenciar vendas do tipo Sedex"
        />

        {/* Filtros */}
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2"
              >
                <Filter className="h-4 w-4" />
                {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
              </Button>
              {showFilters && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearFilters}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Limpar
                  </Button>
                  <Button size="sm" onClick={handleApplyFilters}>
                    <Search className="h-4 w-4 mr-1" />
                    Buscar
                  </Button>
                </div>
              )}
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Período</Label>
                  <Select
                    value={datePeriod}
                    onValueChange={handleDatePeriodChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o período" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Hoje</SelectItem>
                      <SelectItem value="yesterday">Ontem</SelectItem>
                      <SelectItem value="thisWeek">Essa Semana</SelectItem>
                      <SelectItem value="thisMonth">Esse Mês</SelectItem>
                      <SelectItem value="last7days">Últimos 7 dias</SelectItem>
                      <SelectItem value="custom">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {datePeriod === "custom" && (
                  <>
                    <div>
                      <Label>Data Inicial</Label>
                      <Input
                        type="date"
                        value={filters.start_date || ""}
                        onChange={(e) =>
                          setFilters({ ...filters, start_date: e.target.value })
                        }
                      />
                    </div>

                    <div>
                      <Label>Data Final</Label>
                      <Input
                        type="date"
                        value={filters.end_date || ""}
                        onChange={(e) =>
                          setFilters({ ...filters, end_date: e.target.value })
                        }
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label>Status Sedex</Label>
                  <MultiSelectCheckbox
                    options={sedexStatuses.map((status) => ({
                      value: status.id.toString(),
                      label: status.name,
                    }))}
                    selectedValues={filters.sedex_status_id || []}
                    onSelectionChange={(values) =>
                      setFilters({ ...filters, sedex_status_id: values })
                    }
                    placeholder="Todos os status"
                    selectAllLabel="Todos"
                  />
                </div>

                <div>
                  <Label>Vendedor</Label>
                  <MultiSelectCheckbox
                    options={sellers.map((seller: any) => ({
                      value: seller.id.toString(),
                      label: seller?.user?.name || "",
                    }))}
                    selectedValues={filters.seller_id || []}
                    onSelectionChange={(values) =>
                      setFilters({ ...filters, seller_id: values })
                    }
                    placeholder="Todos os vendedores"
                    selectAllLabel="Todos"
                    searchable
                    searchPlaceholder="Buscar vendedor..."
                  />
                </div>

                <div>
                  <Label>Código de Rastreio</Label>
                  <Input
                    placeholder="Buscar por código..."
                    value={filters.tracking_code || ""}
                    onChange={(e) =>
                      setFilters({ ...filters, tracking_code: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label>ID da Venda</Label>
                  <Input
                    placeholder="Buscar por ID..."
                    value={filters.sale_id || ""}
                    onChange={(e) =>
                      setFilters({ ...filters, sale_id: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label>Cliente</Label>
                  <Input
                    placeholder="Nome do cliente..."
                    value={filters.client_name || ""}
                    onChange={(e) =>
                      setFilters({ ...filters, client_name: e.target.value })
                    }
                  />
                </div>

                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="only-edited"
                    checked={filters.only_edited || false}
                    onChange={(e) =>
                      setFilters({ ...filters, only_edited: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="only-edited" className="cursor-pointer text-blue-600 font-medium">
                    Apenas editadas
                  </Label>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabela */}
        {state === "loading" ? (
          <CreateLoaderTable cols={8} lines={6} />
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Valor Total</TableHead>
                    <TableHead>Previsão de Chegada</TableHead>
                    <TableHead>Codigo Rastreio</TableHead>
                    <TableHead>Status Sedex</TableHead>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listData?.data?.length > 0 ? (
                    listData.data.map((sale: SedexSale) => (
                      <TableRow
                        key={sale.id}
                        className={
                          sale.sale_status_id === SALE_STATUS.EDITED
                            ? "bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-500"
                            : ""
                        }
                      >
                        <TableCell className="font-medium">#{sale.id}</TableCell>
                        <TableCell>{sale.client?.name || "N/A"}</TableCell>
                        <TableCell>{sale.seller?.user?.name || "N/A"}</TableCell>
                        <TableCell>
                          {Mask.formatCurrency(parseFloat(sale.total))}
                        </TableCell>
                        <TableCell>
                          {sale.expected_arrival_date
                            ? (() => {
                                // Evita problema de timezone ao parsear data
                                const [year, month, day] = sale.expected_arrival_date.split('-');
                                return `${day}/${month}/${year}`;
                              })()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {sale.tracking_code || (
                            <span className="text-red-500 text-sm">
                              Sem código
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge
                              className={`${getStatusBadgeColor(
                                sale.sedex_status_id
                              )} text-white`}
                            >
                              {sale.sedex_status?.name || "Não definido"}
                            </Badge>
                            {sale.sale_status_id === SALE_STATUS.EDITED && (
                              <Badge className="bg-blue-100 text-blue-800 text-[10px]">
                                Editada
                              </Badge>
                            )}
                            {sale.sale_type_id !== 4 && sale.sale_type && (
                              <Badge className="bg-orange-100 text-orange-800 text-[10px]">
                                Agora: {sale.sale_type.name}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            {formatDate(sale.created_at)}
                            {sale.sale_status_id === SALE_STATUS.EDITED && sale.updated_by && (
                              <div className="mt-1 text-xs text-gray-500">
                                <div>Por: {sale.updated_by.name}</div>
                                <div className="text-[10px]">
                                  {new Date(sale.updated_at).toLocaleDateString("pt-BR")} às{" "}
                                  {new Date(sale.updated_at).toLocaleTimeString("pt-BR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => openEditModal(sale)}
                              title="Editar Status"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => openPrintModal(sale)}
                              title="Imprimir Etiqueta"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        Nenhuma venda Sedex encontrada
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {listData?.last_page > 1 && (
                <div className="p-4 border-t">
                  <Pagination
                    current_page={listData?.current_page || 1}
                    last_page={listData?.last_page || 1}
                    handleNext={() => fetchSales(listData?.current_page + 1)}
                    handlePrevious={() => fetchSales(listData?.current_page - 1)}
                    handleLastPage={() => fetchSales(listData?.last_page)}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Modal de Editar Status */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Atualizar Status do Sedex
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Código de Rastreio *</Label>
                <Input
                  placeholder="Ex: BR123456789BR"
                  value={editForm.tracking_code}
                  onChange={(e) =>
                    setEditForm({ ...editForm, tracking_code: e.target.value })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Obrigatório para avançar o status
                </p>
              </div>

              <div>
                <Label>Status do Sedex</Label>
                <Select
                  value={editForm.sedex_status_id}
                  onValueChange={(value) =>
                    setEditForm({ ...editForm, sedex_status_id: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    {sedexStatuses.map((status) => (
                      <SelectItem key={status.id} value={status.id.toString()}>
                        {status.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Previsão de Chegada</Label>
                <Input
                  type="date"
                  value={editForm.expected_arrival_date}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      expected_arrival_date: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdateStatus}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Impressão */}
        <Dialog open={printModalOpen} onOpenChange={setPrintModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Printer className="h-5 w-5" />
                Imprimir Etiqueta do Destinatário
              </DialogTitle>
            </DialogHeader>

            <div
              ref={printRef}
              className="bg-white p-6"
              style={{ maxWidth: "350px" }}
            >
              {printData && (
                <div className="flex flex-col uppercase" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                  {/* Cabeçalho */}
                  <div className="mb-4 border-b-2 border-black pb-2">
                    <p className="font-bold text-base">DESTINATÁRIO</p>
                  </div>

                  {/* Nome do Cliente */}
                  <div className="mb-3">
                    <p className="font-bold text-base">
                      {printData.client_name?.toUpperCase()}
                    </p>
                  </div>

                  {/* Endereço */}
                  <div className="flex-1">
                    {printData.address ? (
                      <div className="space-y-1">
                        {/* Rua, Número e Complemento */}
                        <p className="font-medium">
                          {printData.address.street?.toUpperCase()}
                          {printData.address.number && `, ${printData.address.number}`}
                          {printData.address.complement && ` - ${printData.address.complement.toUpperCase()}`}
                        </p>
                        {/* Bairro */}
                        {printData.address.neighborhood && (
                          <p className="font-medium">{printData.address.neighborhood.toUpperCase()}</p>
                        )}
                        {/* Cidade - Estado - CEP */}
                        <p className="font-medium">
                          {printData.address.city?.toUpperCase()}
                          {printData.address.state && ` - ${printData.address.state.toUpperCase()}`}
                          {printData.address.zip_code && ` - CEP: ${printData.address.zip_code}`}
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic normal-case">Endereço não informado</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPrintModalOpen(false)}>
                Fechar
              </Button>
              <Button onClick={() => handlePrint()} className="gap-2">
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </If>
    </Main>
  );
};
